import { SearchInput } from "PFApp/components/search_input";
import { NotificationsSettingsForm } from "PFApp/notifications_settings_form/notifications_settings_form";
import { useNotificationRows } from "PFApp/notifications_settings_form/use_notification_rows";
import { useNotificationsSettingsForm } from "PFApp/notifications_settings_form/use_notifications_settings_form";
import ProfileEditPanel from "PFApp/profiles/edit/profile_edit_panel";
import { getProfileSectionText } from "PFApp/profiles/edit/profile_edit_sections";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { useProfileNotificationsSettings } from "PFCore/hooks/queries/notifications/use_profile_notifications_settings";
import { useProfileNotificationsSettingsUpdate } from "PFCore/hooks/queries/notifications/use_profile_notifications_settings_update";
import { ProfileNotificationSetting } from "PFTypes";
import { RecentSearchesPageKey } from "PFTypes/recent_searches";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const NotificationsSection = ({
  overlayContainerRef
}: {
  overlayContainerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const { t } = useTranslation("profiles");
  const { t: tCommon } = useTranslation();
  const { t: tNotifications } = useTranslation("notificationsSettings");
  const { showGrowl } = useGrowlStoreActions();
  const { data: notificationsSettings } = useProfileNotificationsSettings();
  const { mutateAsync: updateNotificationsSettings } = useProfileNotificationsSettingsUpdate();
  const [term, setTerm] = useState("");
  const [searchDisabled, setSearchDisabled] = useState(false);

  const { allRows, filteredRows, foundCount, expandedState, setExpandedState } = useNotificationRows(
    notificationsSettings ?? [],
    term
  );

  const handleMutationSubmit = (
    formValues: Pick<ProfileNotificationSetting, "id" | "frequency" | "important">[]
  ) => {
    updateNotificationsSettings({ notificationSettings: formValues })
      .then(() => {
        showGrowl({ kind: "success", message: t("edit.sections.settingsUpdatedMessage") });
      })
      .catch(() => {
        showGrowl({ kind: "error", message: t("edit.sections.settingsUpdateErrorMessage") });
      });
  };

  const { formMethods, isDirty, handleSubmit } = useNotificationsSettingsForm({
    notificationsSettings: notificationsSettings ?? [],
    onSubmit: handleMutationSubmit
  });

  return (
    <ProfileEditPanel title={getProfileSectionText("notifications")}>
      <Stack>
        <Inline alignItems="center" gap="spacingMd">
          <SearchInput
            term={term}
            onTermChange={setTerm}
            disabled={searchDisabled}
            pageKey={RecentSearchesPageKey.NotificationsSettings}
            includeRecentSearches={false}
          />
          {!!foundCount && (
            <Typography variant="labelRegular">
              {tNotifications("search.found", { count: foundCount })}
            </Typography>
          )}
        </Inline>
        {notificationsSettings ? (
          <>
            <Stack gap="spacingLg" center>
              <NotificationsSettingsForm
                notificationsSettings={notificationsSettings}
                formMethods={formMethods}
                allRows={allRows}
                filteredRows={filteredRows}
                expandedState={expandedState}
                setExpandedState={setExpandedState}
                onSelectionChange={(selected) => setSearchDisabled(selected.length > 0)}
                overlayContainerRef={overlayContainerRef}
                showImportantColumn
              />
              <Button onClick={handleSubmit} disabled={!isDirty} text={tCommon("save")} />
            </Stack>
          </>
        ) : (
          <Inline center>
            <LoadingDots circlesEnabled circleSize="xs" />
          </Inline>
        )}
      </Stack>
    </ProfileEditPanel>
  );
};
